import { AbTest } from '../../../models/AbTest';
import { AbTestOptimizationTarget } from '@pdqdev/pdq-shared/dist/entities';
import { getKeyByStringValue } from '@utils/string-utils';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { RuleEngineApiDto } from '../../../models/AbTestRulesApiDto';
import { AbTestWidget } from '../../../models/AbTestWidget';
import { AbTestTypeCategory, AbTestTypeCategoryToModalTitle } from '../../../models/AbTestCreationType';
import React from 'react';

export interface AbTestFormData {
  name: string;
  description?: string;
  numberOfCheckouts: number;
  optimizingFor: string;
  statisticalSignificance: number;
  abTestVariants: TrafficAllocationVariant[];
  abTestRules?: RuleEngineApiDto;
}

export const getInitialFormData = (existingAbTest?: AbTest): AbTestFormData => {
  return {
    name: existingAbTest?.name || '',
    description: existingAbTest?.description || '',
    numberOfCheckouts: existingAbTest?.numberOfCheckouts,
    optimizingFor: existingAbTest
      ? existingAbTest.optimizingFor
      : getKeyByStringValue(AbTestOptimizationTarget, AbTestOptimizationTarget.CHECKOUT_CONVERSION),
    statisticalSignificance: existingAbTest ? existingAbTest?.statisticalSignificance : 95,
    abTestVariants: [],
    abTestRules: existingAbTest?.abTestRules,
  };
};

export function getTitle(mode: string, testTypeCategory?: AbTestTypeCategory): React.ReactNode {
  if (mode === 'create') {
    return AbTestTypeCategoryToModalTitle[testTypeCategory];
  }

  const modeMap = {
    create: 'New A/B test',
    edit: 'Edit A/B test',
    view: 'View A/B test',
    duplicate: 'Duplicate A/B test',
  };

  return modeMap[mode] || 'View A/B test';
}

export function getButtonText(mode: string): string {
  const modeMap = {
    create: 'Create draft test',
    edit: 'Save draft test',
    duplicate: 'Create draft test',
    view: 'Done',
  };

  return modeMap[mode] || 'Save draft test';
}

export const validateDetails = (formData: AbTestFormData): boolean => {
  return (
    formData?.name?.length > 0 &&
    formData?.description?.replace(/\s/g, '').length >= 100 &&
    formData?.statisticalSignificance > 0
  );
};

export const validateStatisticalSignificance = (value: string): string | undefined => {
  if (isNaN(Number(value)) || Number(value) <= 0 || Number(value) > 100) {
    return 'Statistical significance must be between 1 and 100';
  }
  return undefined;
};

export const validateNumberOfCheckouts = (value: string): boolean => {
  return !isNaN(Number(value)) && Number(value) >= 0;
};

export function buildAbTestWidgetsForVariant(
  variant: TrafficAllocationVariant,
  widgetType: string,
  extraWidgetFields: Partial<AbTestWidget> = {},
): AbTestWidget[] {
  const selections = variant.widgetSelections ?? [
    { widgetExternalId: variant.widgetExternalId, widgetApiId: variant.widgetApiId },
  ];
  // the api dereferences abTestWidgets[0], so an empty set ships as the legacy NONE widget rather than []
  const selectionsToEmit = selections.length
    ? selections
    : [{ widgetExternalId: null, widgetApiId: variant.widgetApiId }];

  return selectionsToEmit.map((selection) => ({
    id: selection.widgetApiId ? selection.widgetApiId : undefined,
    externalId: selection.widgetExternalId,
    type: widgetType,
    ...extraWidgetFields,
  }));
}

function buildExtensionSetSignature(variant: TrafficAllocationVariant): string {
  const externalIds = (variant.widgetSelections ?? []).map((selection) => selection.widgetExternalId);
  return Array.from(new Set(externalIds))
    .sort((a, b) => a - b)
    .join(',');
}

export function haveVariantsWithIdenticalExtensionSets(variants: TrafficAllocationVariant[]): boolean {
  const signatures = variants.map(buildExtensionSetSignature);
  return new Set(signatures).size < signatures.length;
}

const MAX_NAMED_EXTENSIONS_IN_SUMMARY = 3;

export function formatSelectedExtensionNames(extensionNames: string[]): string {
  const namedExtensions = extensionNames.slice(0, MAX_NAMED_EXTENSIONS_IN_SUMMARY);
  const overflowCount = extensionNames.length - namedExtensions.length;
  if (overflowCount <= 0) {
    return namedExtensions.join(', ');
  }
  return `${namedExtensions.join(', ')}, +${overflowCount} extension${overflowCount === 1 ? '' : 's'}`;
}
